import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'

const MAX_MOTIVO = 500

interface Props {
  open: boolean
  titulo: string
  descricao?: string
  confirmar: string
  /** Executa a ação; erro lançado vira a mensagem do diálogo (como o backend mandou). */
  onConfirmar: (motivo: string) => Promise<void>
  onFechar: () => void
  /** Texto do erro a partir da exceção. */
  mensagemErro: (e: unknown) => string
}

/** Ação do back-office que só pede um motivo (cancelar solicitação, atualizar versão). */
export const MotivoDialog: FC<Props> = ({ open, titulo, descricao, confirmar, onConfirmar, onFechar, mensagemErro }) => {
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setMotivo('')
      setErro(null)
    }
  }, [open])

  const podeSalvar = !salvando && motivo.trim().length > 0 && motivo.length <= MAX_MOTIVO

  const salvar = async () => {
    if (!podeSalvar) return
    setSalvando(true)
    setErro(null)
    try {
      await onConfirmar(motivo.trim())
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={open} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{titulo}</DialogTitle>
      <DialogContent>
        {descricao && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {descricao}
          </Typography>
        )}
        <TextField
          label="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          required
          fullWidth
          multiline
          minRows={2}
          disabled={salvando}
          error={motivo.length > MAX_MOTIVO}
          helperText={`Obrigatório — ${motivo.length}/${MAX_MOTIVO}`}
        />
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {confirmar}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MotivoDialog
